import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Block } from "../lib/blocks";
import type { Message, ToolCall } from "../lib/api";
import { isInterrupted, parseSessionLimit } from "../lib/chatErrors";
import { Markdown } from "./Markdown";
import { ToolCard } from "./ToolCard";

/** How close to the bottom still counts as "following the stream". */
const STICK_PX = 96;

function toolFromBlock(b: Extract<Block, { kind: "tool" }>): ToolCall {
  return {
    id: b.toolId,
    toolId: b.toolId,
    name: b.name,
    status: b.status,
    input: b.input,
    output: b.output,
  };
}

function isLastTextBlock(blocks: Block[], target: Extract<Block, { kind: "text" }>): boolean {
  for (let i = blocks.length - 1; i >= 0; i--) {
    const b = blocks[i];
    if (b?.kind === "text") return b === target;
  }
  return false;
}

function ThinkingBlock({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <>
      <button
        type="button"
        className="think__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="think__dot" />
        {open ? "Hide reasoning" : "Reasoning"}
      </button>
      {open && <div className="think__body">{text}</div>}
    </>
  );
}

function BlockView({
  block: b,
  streaming,
  blocks,
}: {
  block: Block;
  streaming: boolean;
  blocks: Block[];
}) {
  if (b.kind === "thinking") return <ThinkingBlock text={b.text} />;
  if (b.kind === "tool") return <ToolCard tool={toolFromBlock(b)} />;
  const showCaret = streaming && isLastTextBlock(blocks, b);
  if (!b.text && !showCaret) return null;
  return (
    <div className="bubble bubble--assistant">
      {b.text ? <Markdown text={b.text} /> : null}
      {showCaret && <span className="caret" aria-hidden="true" />}
    </div>
  );
}

function WorkedOnIt({ lead, failed }: { lead: Block[]; failed: number }) {
  const [open, setOpen] = useState(failed > 0);
  const steps = lead.filter((b) => b.kind === "tool").length;
  const label = `Worked on it · ${steps} step${steps === 1 ? "" : "s"}${
    failed ? ` · ${failed} failed` : ""
  }`;
  return (
    <div className="work">
      <button
        type="button"
        className="work__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="work__text">{label}</span>
        <span className={`tool__chev ${open ? "is-open" : ""}`} aria-hidden="true">
          ›
        </span>
      </button>
      {open && (
        <div className="work__body">
          {lead.map((b) => (
            <BlockView key={`${b.kind}-${b.seq}`} block={b} streaming={false} blocks={lead} />
          ))}
        </div>
      )}
    </div>
  );
}

function Bubble({
  msg,
  busy,
  onRetry,
  retryUser,
}: {
  msg: Message;
  busy: boolean;
  onRetry?: (seq: number, text: string) => void;
  retryUser?: Message;
}) {
  const isUser = msg.role === "user";
  const streaming = msg.status === "streaming";
  const blocks = msg.blocks ?? [];

  if (isUser) {
    return (
      <div className="msg msg--user">
        <div className="bubble bubble--user">
          {msg.attachments.length > 0 && (
            <div className="bubble__shots">
              {msg.attachments.map((a, i) => (
                <img key={a.id ?? i} src={`/api/attachments/${a.id ?? ""}`} alt={a.name} loading="lazy" />
              ))}
            </div>
          )}
          {msg.content && <div className="bubble__text">{msg.content}</div>}
        </div>
        {onRetry && (
          <div className="msg__actions">
            <button
              type="button"
              className="msg__action"
              onClick={() => onRetry(msg.seq, msg.content)}
              disabled={busy}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  const lastTool = blocks.reduce((idx, b, i) => (b.kind === "tool" ? i : idx), -1);
  const collapse = !streaming && lastTool >= 0;
  const lead = collapse ? blocks.slice(0, lastTool + 1) : [];
  const rest = collapse ? blocks.slice(lastTool + 1) : blocks;
  const failed = lead.filter((b) => b.kind === "tool" && b.status === "failed").length;

  return (
    <div className="msg msg--assistant">
      {collapse && <WorkedOnIt lead={lead} failed={failed} />}
      {rest.map((b) => (
        <BlockView key={`${b.kind}-${b.seq}`} block={b} streaming={streaming} blocks={blocks} />
      ))}

      {streaming && blocks.length === 0 && (
        <div className="bubble bubble--assistant">
          <span className="thinking-dots" aria-label="Thinking">
            <i /><i /><i />
          </span>
        </div>
      )}

      {(() => {
        const quota = parseSessionLimit(msg.error);
        const interrupted = isInterrupted(msg.status, msg.error);
        const retry = () => {
          if (onRetry && retryUser) onRetry(retryUser.seq, retryUser.content);
        };
        if (quota) {
          return (
            <div className="msg__quota" role="status">
              <strong>Claude session limit reached</strong>
              {quota.resetAt ? <span>Resets {quota.resetAt}.</span> : null}
              <span>Switch the engine to Grok to keep going — retrying will not work until the reset.</span>
            </div>
          );
        }
        if (interrupted) {
          return (
            <div className="msg__note">
              Interrupted when the server restarted.
              {onRetry && retryUser && (
                <button type="button" className="btn" onClick={retry} disabled={busy}>
                  Retry
                </button>
              )}
            </div>
          );
        }
        if (msg.error) {
          return (
            <div className="msg__error">
              <strong>Turn failed</strong>
              <span>{msg.error}</span>
              {onRetry && retryUser && (
                <button type="button" className="btn" onClick={retry} disabled={busy}>
                  Retry
                </button>
              )}
            </div>
          );
        }
        if (msg.status === "stopped") return <div className="msg__note">Stopped</div>;
        return null;
      })()}
    </div>
  );
}

export function Thread({
  messages,
  busy,
  intro,
  onRetry,
  reconnecting,
}: {
  messages: Message[];
  busy: boolean;
  intro?: string;
  onRetry?: (seq: number, text: string) => void;
  reconnecting?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [showJump, setShowJump] = useState(false);

  // Track intent: once the user scrolls up they own the viewport until they
  // come back down. Yanking them to the bottom mid-read is the classic sin.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onScroll = () => {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      stick.current = gap < STICK_PX;
      setShowJump(gap > 240);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // iOS settles keyboard geometry over several frames; a single immediate
  // scroll on kb-open lands short, so do next-frame and again after 250ms.
  useEffect(() => {
    const root = document.documentElement;
    let wasOpen = root.classList.contains("kb-open");
    const toEnd = () => {
      const el = box.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    };
    const onClass = () => {
      const open = root.classList.contains("kb-open");
      if (open && !wasOpen) {
        requestAnimationFrame(toEnd);
        window.setTimeout(toEnd, 250);
      }
      wasOpen = open;
    };
    const obs = new MutationObserver(onClass);
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Layout effect so the scroll lands in the same frame as the new text —
  // doing it in useEffect shows one frame of the pre-scroll position.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !stick.current) return;
    el.scrollTop = el.scrollHeight;
  });

  const jump = () => {
    const el = box.current;
    if (!el) return;
    stick.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  return (
    <div className="thread-wrap">
      <div className="thread scroll" ref={box}>
        <div className="thread__inner">
          {messages.length === 0 && intro && (
            <div className="thread__intro">
              <h2>Ask anything about the stack</h2>
              <p>{intro}</p>
            </div>
          )}
          {messages.map((m, i) => {
            const prevUser =
              m.role === "assistant"
                ? [...messages.slice(0, i)].reverse().find((x) => x.role === "user")
                : undefined;
            return (
              <Bubble
                key={m.id}
                msg={m}
                busy={busy}
                onRetry={onRetry}
                retryUser={prevUser}
              />
            );
          })}
          {reconnecting && (
            <div className="msg__lost" role="status">
              Connection lost — reconnecting
            </div>
          )}
        </div>
      </div>

      {showJump && (
        <button type="button" className="jump" onClick={jump} aria-label="Jump to latest">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M19 12l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
}
